import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { FIELD_TYPE } from "@/features/forms/models";
import { onboardingQueriesStore } from "@/features/onboarding/api";
import { ONBOARDING_ROUTE } from "@/features/onboarding/router";
import { useOnboardingStore } from "@/features/onboarding/stores";
import { useBoolean } from "@/shared/hooks";
import type { Answer, Question } from "@/shared/models";

interface UseEligibilityQuestionsOptions {
  onDisqualified?: VoidFunction;
}

export const useEligibilityQuestions = (
  bmi?: number,
  { onDisqualified }: UseEligibilityQuestionsOptions = {},
) => {
  const setEligibilityAnswerIds = useOnboardingStore(
    (state) => state.setEligibilityAnswerIds,
  );

  useEffect(() => {
    setEligibilityAnswerIds(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigate = useNavigate();

  const {
    value: disqualified,
    setTrue: setDisqualifiedTrue,
    setFalse: setDisqualifiedFalse,
  } = useBoolean(false);
  const [nextQuestionId, setNextQuestionId] = useState<Question["id"]>();
  const [history, setHistory] = useState<Question["id"][]>([]);
  const [answersMap, setAnswersMap] = useState<
    Map<Question["id"], Answer["id"][]>
  >(new Map());

  const { data: initialQuestion, isLoading: isLoadingInitialQuestion } =
    useQuery(
      onboardingQueriesStore.onboarding.filteringQuestions._ctx.initial(bmi),
    );

  const { data: nextQuestion, isLoading: isLoadingNextQuestion } = useQuery(
    onboardingQueriesStore.onboarding.filteringQuestions._ctx.next(
      nextQuestionId,
    ),
  );

  const question = nextQuestion ?? initialQuestion;
  const isLoading = isLoadingInitialQuestion || isLoadingNextQuestion;

  const saveAnswers = (questionId: Question["id"], answers: Answer["id"][]) => {
    setAnswersMap((prev) => {
      const updatedMap = new Map(prev);
      updatedMap.set(questionId, answers);
      return updatedMap;
    });
  };

  const handleAnswers = (answers: Answer["id"][]) => {
    if (!question) {
      return;
    }

    const selectedAnswers = question.onboardingAnswers.filter((answer) =>
      answers.includes(answer.id),
    );

    if (selectedAnswers.some((answer) => answer.disqualifier)) {
      setDisqualifiedTrue();
      onDisqualified?.();
      return;
    }

    if (selectedAnswers[0]?.nextAnswerGroupId) {
      setHistory((prev) => [...prev, question.id]);
      saveAnswers(question.id, answers);
      setNextQuestionId(selectedAnswers[0].nextAnswerGroupId);
      return;
    }

    const answerIds = [...answersMap.values()].flat();
    setEligibilityAnswerIds(answerIds);

    navigate(ONBOARDING_ROUTE.ACCOUNT_PERSONAL_DETAILS, {
      state: { answers },
    });
  };

  const handleGoBack = () => {
    setDisqualifiedFalse();

    if (question) {
      setAnswersMap((prev) => {
        const updated = new Map(prev);
        updated.delete(question.id);
        return updated;
      });
    }

    if (history.length === 0) {
      return;
    }

    setHistory((prev) => {
      const updatedHistory = [...prev];
      const lastQuestionId = updatedHistory.pop();

      setNextQuestionId(lastQuestionId);
      return updatedHistory;
    });
  };

  const getAnswersForQuestion = (questionId: Question["id"]) =>
    answersMap.get(questionId) ?? [];

  const setAnswersForQuestion = (
    questionId: Question["id"],
    newAnswers: Answer["id"][],
  ) => {
    setAnswersMap((prev) => {
      const updated = new Map(prev);
      updated.set(questionId, newAnswers);

      const hasUniqueAnswer = question?.onboardingAnswers.some((option) =>
        newAnswers.some((v) => v === option.id && option.unique),
      );
      const isRadioGroup = question?.type === FIELD_TYPE.RADIO;

      if (hasUniqueAnswer || isRadioGroup) {
        handleAnswers(newAnswers);
      }

      return updated;
    });
  };

  return {
    question,
    disqualified,
    isLoading,
    handleAnswers,
    handleGoBack,
    getAnswersForQuestion,
    setAnswersForQuestion,
  };
};
